import { brandingToTokens, type TenantBranding } from '@auctisync/color';
import { Logo } from '@auctisync/ui';
import type { CSSProperties, ReactNode } from 'react';
import { tenantDisplayName, useTenant, type TenantContextValue } from '../contexts/TenantContext';

/**
 * Shared Layout for the help center.
 *
 * Platform mode (help.auctisync.com): AuctiSync-branded nav + footer with
 * links back to auctisync.com.
 *
 * White-label mode (any tenant custom domain): tenant.display_name + tenant
 * logo (if set in metadata.branding.logo_url) + nav links to the tenant's
 * own marketing site + tenant brand color override via CSS custom property.
 */
export function Layout({ children }: { children: ReactNode }) {
  const tenant = useTenant();
  const styleOverrides = tenantStyleOverrides(tenant);
  return (
    <div className="flex min-h-full flex-col" style={styleOverrides}>
      <Nav tenant={tenant} />
      <main className="flex-1">{children}</main>
      <Footer tenant={tenant} />
    </div>
  );
}

/**
 * CSS custom-property overrides driven by tenant branding. Empty object in
 * platform mode = uses the default theme from @auctisync/ui.
 *
 * Uses the canonical @auctisync/color mapping — the SINGLE source of truth so a
 * tenant's primary_color maps to --brand-primary (not --brand-accent, the old
 * bug here) with AA-guaranteed derived shades, consistent with every other
 * surface. The help center renders light-mode.
 */
function tenantStyleOverrides(tenant: TenantContextValue | null): CSSProperties {
  if (!tenant) return {};
  return brandingToTokens(tenant.branding as TenantBranding, 'light') as CSSProperties;
}

function Nav({ tenant }: { tenant: TenantContextValue | null }) {
  const name = tenantDisplayName(tenant);
  const logoUrl = typeof tenant?.branding.logo_url === 'string' ? tenant.branding.logo_url : null;
  const mainSiteUrl = tenantMainSiteUrl(tenant);

  return (
    <header className="border-b" style={{ borderColor: 'var(--border)' }}>
      <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
        <a
          href="/"
          className="flex items-center gap-3 text-lg font-bold tracking-tight"
          style={{ color: 'var(--text)' }}
        >
          {tenant === null ? (
            <span className="flex items-center gap-2">
              <Logo height={22} />
              <span className="text-sm font-normal" style={{ color: 'var(--text-muted)' }}>
                Help Center
              </span>
            </span>
          ) : (
            <>
              {logoUrl ? <img src={logoUrl} alt="" className="h-8 w-auto" /> : null}
              <span className="flex items-baseline gap-2">
                {name}
                <span className="text-sm font-normal" style={{ color: 'var(--text-muted)' }}>
                  Help Center
                </span>
              </span>
            </>
          )}
        </a>
        <nav
          className="hidden items-center gap-6 text-sm md:flex"
          style={{ color: 'var(--text-muted)' }}
        >
          <a href="/ask" className="font-semibold hover:text-[color:var(--text)]">
            Ask {name}
          </a>
          {/*
            In platform mode show auctisync.com / Pricing / Contact.
            In white-label mode show the tenant's main site (single link).
            Tenants can later add more nav links via branding metadata if needed.
          */}
          {tenant ? (
            mainSiteUrl ? (
              <a href={mainSiteUrl} className="hover:text-[color:var(--text)]">
                {name}
              </a>
            ) : null
          ) : (
            <>
              <a href="https://auctisync.com" className="hover:text-[color:var(--text)]">
                auctisync.com
              </a>
              <a href="https://auctisync.com/pricing" className="hover:text-[color:var(--text)]">
                Pricing
              </a>
              <a href="https://auctisync.com/contact" className="hover:text-[color:var(--text)]">
                Contact
              </a>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

function Footer({ tenant }: { tenant: TenantContextValue | null }) {
  const year = new Date().getFullYear();
  const name = tenantDisplayName(tenant);
  return (
    <footer className="border-t py-8" style={{ borderColor: 'var(--border)' }}>
      <div
        className="mx-auto flex max-w-5xl flex-col items-start justify-between gap-4 px-6 md:flex-row md:items-center"
        style={{ color: 'var(--text-muted)' }}
      >
        <div className="text-sm">
          © {year} {name}. Help Center docs ship with every feature module.
        </div>
        {tenant ? null : (
          <div className="flex gap-4 text-sm">
            <a href="https://auctisync.com/about" className="hover:text-[color:var(--text)]">
              About
            </a>
            <a
              href="https://auctisync.com/case-studies/dta"
              className="hover:text-[color:var(--text)]"
            >
              Case Study
            </a>
          </div>
        )}
      </div>
    </footer>
  );
}

/**
 * Resolve a tenant's main marketing site URL. Falls back to the tenant's
 * apex custom domain when `branding.main_site_url` isn't set — a reasonable
 * default since the apex is usually the tenant's main site.
 */
function tenantMainSiteUrl(tenant: TenantContextValue | null): string | null {
  if (!tenant) return null;
  const explicit = tenant.branding.main_site_url;
  if (typeof explicit === 'string' && explicit.length > 0) return explicit;
  return `https://${tenant.custom_domain_apex}`;
}
